import { useEffect, useState } from 'react';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpAppsecEditPermission } from '@internal/ksdp-permissions-common';
import { Application, Report } from './types';
import { SecuritySummaryAssessmentResults } from './SecuritySummaryAssessmentResults';
import { hasEvaluationData } from './scoring';
import { formatDateTime } from './formatDateTime';

/** Resolves the download link for a report — see EvaluationDetails' prior
 * inline use of this same fallback chain, now also needed one level up
 * (next to the report-picker dropdown) since the button moved there. */
function getDownloadUrl(report: Report): string {
  return report.report_url ?? report.url ?? '';
}

export interface SecuritySummaryViewProps {
  app: Application;
  onBack: () => void;
  evaluationStatus: string | null;
  submitting: boolean;
  onRequestEvaluation: () => Promise<void> | void;
}

function formatTimestamp(iso: string): string {
  return formatDateTime(iso);
}

/**
 * Security Summary module view — link + version/significant-changes
 * badges, the Security Summary Assessment Results charts (for whichever report is
 * currently selected), the evaluation report dropdown/details, and the
 * dynamic "Request Evaluation..." button.
 *
 * Ported from dummy_webpage/current/view2.js's
 * renderSecuritySummaryViewHtml()/renderEvaluationBody()/
 * renderEvaluationDetails()/renderRequestEvaluationButton(), wired to real
 * data (app.reports from GET /applications/{ban}, enriched by
 * ksdp-get-reports' join for content_id/requested_by/
 * security_summary_version) instead of the prototype's mockApplication.
 *
 * Evaluation request networking (submit + status polling) is owned by the
 * parent BusinessApplicationDetailsPage — this component only renders the
 * button and calls back into onRequestEvaluation, matching how the real
 * (non-dummy) BusinessApplicationDetailsPage.tsx already handled it before
 * this redesign.
 */
export function SecuritySummaryView({
  app,
  onBack,
  evaluationStatus,
  submitting,
  onRequestEvaluation,
}: SecuritySummaryViewProps) {
  const reports = app.reports ?? [];
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(
    reports[0]?.timestamp ?? null,
  );

  useEffect(() => {
    if (
      reports.length > 0 &&
      !reports.some(r => r.timestamp === selectedTimestamp)
    ) {
      setSelectedTimestamp(reports[0].timestamp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const selectedReport: Report | undefined = reports.find(
    r => r.timestamp === selectedTimestamp,
  );

  const hasVersion =
    app.latest_security_summary_version !== null &&
    app.latest_security_summary_version !== undefined;
  const alreadyEvaluated = Boolean(app.latest_security_summary_evaluated);
  // UI-only gate; POST /evaluation/request is enforced by the backend router.
  const { allowed: canEdit } = usePermission({
    permission: ksdpAppsecEditPermission,
  });
  const showRequestButton = canEdit && hasVersion && !alreadyEvaluated;
  const isRequestInFlight =
    submitting ||
    evaluationStatus === 'IN_PROGRESS' ||
    evaluationStatus === 'REQUESTED';

  return (
    <div className="card detail-view-card">
      <div className="detail-view-header">
        <button type="button" className="back-button" onClick={onBack}>
          <span aria-hidden="true">&#8592;</span> Back to Application Modules
        </button>
        <h4 className="detail-view-title">Security Summary</h4>
      </div>

      <div className="security-summary-link">
        {app.latest_security_summary_link ? (
          <a
            href={app.latest_security_summary_link}
            target="_blank"
            rel="noreferrer"
          >
            Open security summary
          </a>
        ) : (
          <>
            <span>No security summary available</span>
            <p className="security-summary-help">
              Please check that your Confluence security summary document has
              the <code>security_summary</code> label, contains the correct BAN,
              and grants <code>SVC_KSDP_CONFLUENCE@kone.com</code> permission to
              view the document.
            </p>
          </>
        )}
        {app.latest_security_summary_link &&
          app.latest_security_summary_version !== null &&
          app.latest_security_summary_version !== undefined && (
            <>
              <span className="summary-version-badge">
                Version {app.latest_security_summary_version}
              </span>
              {app.significant_changes && (
                <span className="summary-version-badge summary-version-badge--warning">
                  Significant changes
                </span>
              )}
            </>
          )}
      </div>

      <div className="request-evaluation-btn">
        {showRequestButton && (
          <button
            className="action-button"
            type="button"
            disabled={isRequestInFlight}
            onClick={() => onRequestEvaluation()}
          >
            Request Evaluation Of Your Security Summary Version{' '}
            {app.latest_security_summary_version} Through AI
          </button>
        )}
        {evaluationStatus && evaluationStatus !== 'COMPLETED' && (
          <p style={{ marginTop: 8 }}>
            Evaluation status: {evaluationStatus}
            {(evaluationStatus === 'REQUESTED' ||
              evaluationStatus === 'IN_PROGRESS') &&
              ' — checking again shortly...'}
          </p>
        )}
        {evaluationStatus === 'FAILED' && (
          <p style={{ marginTop: 8 }}>
            Evaluation failed. Please try requesting again or contact support.
          </p>
        )}
      </div>

      <h4 className="evaluation-heading">
        Evaluation Results (latest to oldest)
      </h4>

      {reports.length === 0 ? (
        <p>No evaluation available for this application.</p>
      ) : (
        <>
          <div className="inline-actions">
            <select
              className="form-control"
              value={selectedTimestamp ?? ''}
              onChange={e => setSelectedTimestamp(e.target.value)}
            >
              {reports.map(report => {
                const versionLabel =
                  report.security_summary_version !== null &&
                  report.security_summary_version !== undefined
                    ? ` (v${report.security_summary_version})`
                    : '';
                return (
                  <option key={report.timestamp} value={report.timestamp}>
                    {formatTimestamp(report.timestamp)}
                    {versionLabel}
                  </option>
                );
              })}
            </select>
            {selectedReport && hasEvaluationData(selectedReport) && (
              <a
                href={getDownloadUrl(selectedReport)}
                target="_blank"
                rel="noreferrer"
                className="action-button"
              >
                Download Selected Report
              </a>
            )}
          </div>

          {selectedReport && <EvaluationDetails report={selectedReport} />}
        </>
      )}

      <h4 className="evaluation-heading">
        Security Summary Assessment Results
      </h4>
      <SecuritySummaryAssessmentResults report={selectedReport} />
    </div>
  );
}

/**
 * Strips the Backstage entity-ref namespace prefix (e.g. "user:default/")
 * from a requested_by value, leaving just the identifying part (typically
 * an email address) — the namespace is internal Backstage plumbing and
 * not meaningful to show in the UI. Only strips the "user:default/" case
 * specifically, rather than any "kind:namespace/" pattern, since that is
 * the only namespace this field is ever populated with (see
 * the business-applications-backend router, which always forwards
 * forwards httpAuth's userEntityRef).
 */
function formatRequestedBy(requestedBy: string): string {
  return requestedBy.replace(/^user:default\//, '');
}

/**
 * Fetched-from-the-database evaluation detail fields for the currently
 * selected report — Requested By only, rendered once hasEvaluationData() is
 * true. Risk Score and Completeness Score were previously shown here too but
 * were removed per product request — they duplicate the SecuritySummaryAssessmentResults
 * ring/gauge charts rendered above for the same selected report, so showing
 * the raw numbers again here was redundant. Content ID was previously shown
 * here too but was removed per product request — it is internal plumbing (a
 * Confluence page id) with no end-user meaning. The "Download Selected Report"
 * link previously lived here too but was moved up next to the report-picker
 * dropdown in the parent SecuritySummaryView, per product request.
 */
function EvaluationDetails({ report }: { report: Report }) {
  if (!hasEvaluationData(report)) {
    return (
      <p className="evaluation-pending">
        Evaluation requested on {formatTimestamp(report.timestamp)} is still in
        progress. No report is available to download yet.
      </p>
    );
  }

  return (
    <div>
      {report.requested_by && (
        <dl className="evaluation-fields">
          <div className="evaluation-field">
            <dt>Requested By:</dt>
            <dd>{formatRequestedBy(report.requested_by)}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}
